import { client } from ".";

/**
 * Notifications API service for the partner-app bell.
 *
 * All endpoints are scoped via ?recipientType=astrologer so the same
 * server-side controller can serve both user (mobile / as-webapp) and
 * astrologer (partner-app) feeds without splitting routes. The path
 * param :userId in the URL carries the astrologer's _id for this app.
 *
 * Each call uses the shared axios client which auto-refreshes the
 * access token on 401, so callers don't need to thread tokens through
 * manually — they only need to pass the astrologerId.
 */

const BASE = "/api/notifications/feed";

/**
 * GET /api/notifications/feed/:astrologerId?recipientType=astrologer
 * Optional cursor: pass `before` (ISO date string) to fetch older rows.
 * Returns { items, hasMore, unreadCount }.
 */
export const getAstrologerNotifications = async (
  astrologerId,
  { limit = 20, before = null } = {}
) => {
  if (!astrologerId) {
    return { status: 200, data: { items: [], hasMore: false, unreadCount: 0 } };
  }
  try {
    const params = new URLSearchParams({
      recipientType: "astrologer",
      limit: String(limit),
    });
    if (before) params.append("before", before);
    const { status, data } = await client.get(
      `${BASE}/${astrologerId}?${params.toString()}`
    );
    return { status, data: data?.data || { items: [], hasMore: false, unreadCount: 0 } };
  } catch (err) {
    console.error("Notifications fetch failed:", err);
    return {
      status: err.response?.status || 500,
      error: err.message,
      data: { items: [], hasMore: false, unreadCount: 0 },
    };
  }
};

/**
 * GET /api/notifications/feed/:astrologerId/unread-count?recipientType=astrologer
 * Cheap endpoint for the bell badge — no list, just the count.
 */
export const getAstrologerUnreadCount = async (astrologerId) => {
  if (!astrologerId) return { status: 200, data: { unreadCount: 0 } };
  try {
    const { status, data } = await client.get(
      `${BASE}/${astrologerId}/unread-count?recipientType=astrologer`
    );
    return { status, data: data?.data || { unreadCount: 0 } };
  } catch (err) {
    console.error("Unread count fetch failed:", err);
    return {
      status: err.response?.status || 500,
      error: err.message,
      data: { unreadCount: 0 },
    };
  }
};

/**
 * PATCH /api/notifications/feed/:astrologerId/read-all?recipientType=astrologer
 */
export const markAllAstrologerNotificationsRead = async (astrologerId) => {
  if (!astrologerId) return { status: 200, data: { updated: 0 } };
  try {
    const { status, data } = await client.patch(
      `${BASE}/${astrologerId}/read-all?recipientType=astrologer`
    );
    return { status, data: data?.data || { updated: 0 } };
  } catch (err) {
    console.error("Mark all read failed:", err);
    return {
      status: err.response?.status || 500,
      error: err.message,
      data: { updated: 0 },
    };
  }
};

/**
 * PATCH /api/notifications/feed/:astrologerId/:notificationId/read?recipientType=astrologer
 */
export const markAstrologerNotificationRead = async (
  astrologerId,
  notificationId
) => {
  if (!astrologerId || !notificationId) {
    return { status: 200, data: { updated: false } };
  }
  try {
    const { status, data } = await client.patch(
      `${BASE}/${astrologerId}/${notificationId}/read?recipientType=astrologer`
    );
    return { status, data: data?.data || { updated: false } };
  } catch (err) {
    console.error("Mark read failed:", err);
    return {
      status: err.response?.status || 500,
      error: err.message,
      data: { updated: false },
    };
  }
};
